import { useState } from 'react';

export default function CallToAction() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d62dr08v3sjav1bavhqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="relative py-32 bg-gradient-to-br from-[#0a1628] via-[#050a14] to-black overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-[#1e3a8a] rounded-full"
              style={{
                width: `${Math.random() * 4 + 1}px`,
                height: `${Math.random() * 4 + 1}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.5 + 0.3
              }}
            ></div>
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-5xl lg:text-7xl font-bold text-white mb-6">
          Ready to see your demo website?
        </h2>
        <p className="text-2xl text-white/70 mb-12 font-light">
          No commitment. No upfront cost. Just results.
        </p>
        
        <div className="flex flex-col items-center gap-6 mb-16">
          <a href="https://calendly.com/nexoracorp/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-[#0a1628] border border-[#1e3a8a]/50 text-white px-12 py-6 rounded-2xl text-xl font-bold transition-all duration-300 hover:shadow-2xl hover:shadow-[#1e3a8a]/20 hover:scale-105 hover:border-[#1e3a8a] whitespace-nowrap cursor-pointer">
            Book a Demo
            <i className="ri-arrow-right-line text-2xl"></i>
          </a>
          <p className="text-white/70 text-lg">
            or call <a href="tel:626-806-4641" className="font-semibold text-white hover:underline cursor-pointer">626-806-4641</a>
          </p>
        </div>

        <div className="bg-[#050a14] backdrop-blur-sm border border-[#1e3a8a]/20 rounded-3xl p-10 lg:p-12 max-w-2xl mx-auto">
          <h3 className="text-3xl font-bold text-white mb-8">Get in Touch</h3>
          
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
                className="w-full bg-[#0a1628] border border-[#1e3a8a]/30 rounded-xl px-6 py-4 text-white placeholder-white/40 focus:outline-none focus:border-[#1e3a8a] transition-colors text-base"
              />
            </div>
            
            <div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                required
                className="w-full bg-[#0a1628] border border-[#1e3a8a]/30 rounded-xl px-6 py-4 text-white placeholder-white/40 focus:outline-none focus:border-[#1e3a8a] transition-colors text-base"
              />
            </div>
            
            <div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your Phone"
                className="w-full bg-[#0a1628] border border-[#1e3a8a]/30 rounded-xl px-6 py-4 text-white placeholder-white/40 focus:outline-none focus:border-[#1e3a8a] transition-colors text-base"
              />
            </div>
            
            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your business"
                rows={4}
                maxLength={500}
                required
                className="w-full bg-[#0a1628] border border-[#1e3a8a]/30 rounded-xl px-6 py-4 text-white placeholder-white/40 focus:outline-none focus:border-[#1e3a8a] transition-colors resize-none text-base"
              ></textarea>
              <p className="text-white/50 text-sm mt-2 text-right">{formData.message.length}/500</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#0a1628] border border-[#1e3a8a]/50 text-white px-8 py-4 rounded-xl font-bold text-lg hover:border-[#1e3a8a] hover:shadow-lg hover:shadow-[#1e3a8a]/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="bg-[#0a1628] border border-[#1e3a8a]/50 rounded-xl p-4 text-white text-center">
                Thank you! We&apos;ll get back to you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-[#0a1628] border border-red-500/50 rounded-xl p-4 text-white text-center">
                Something went wrong. Please try again.
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32">
        <svg viewBox="0 0 1200 120" className="w-full h-full opacity-10">
          <path d="M0,60 Q300,20 600,60 T1200,60 L1200,120 L0,120 Z" fill="#1e3a8a" />
        </svg>
      </div>
    </section>
  );
}
